import { useState } from "react";
import { useAuth } from "../../context/AuthContext";
import Logo from "../common/Logo";

const FEEDBACK_EMAIL = "ai.insightpulse@gmail.com";

const CATEGORIES = [
  { id: "bug", label: "Bug report" },
  { id: "feature", label: "Feature request" },
  { id: "general", label: "General feedback" },
];

function buildEmailParts(user, category, subject, message) {
  const categoryLabel = CATEGORIES.find(c => c.id === category)?.label || "General feedback";
  const finalSubject = subject.trim() || `InsightPulse AI feedback — ${categoryLabel}`;

  const bodyLines = [
    `Category: ${categoryLabel}`,
    "",
    message.trim(),
    "",
    "----",
    `From: ${user?.displayName || "InsightPulse AI user"}${user?.email ? ` (${user.email})` : ""}`,
  ];

  return { subject: finalSubject, body: bodyLines.join("\n") };
}

export default function FeedbackScreen({ onClose }) {

  const { user } = useAuth();

  const [category, setCategory] = useState("general");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState(null);
  const [copyStatus, setCopyStatus] = useState(null); // null | "copied" | "error"
  const [sent, setSent] = useState(false);

  const handleSend = (e) => {
    e.preventDefault();

    if (!message.trim()) {
      setError("Please add a message before sending.");
      return;
    }

    setError(null);

    const { subject: finalSubject, body } = buildEmailParts(user, category, subject, message);
    const mailtoUrl = `mailto:${FEEDBACK_EMAIL}?subject=${encodeURIComponent(finalSubject)}&body=${encodeURIComponent(body)}`;

    window.location.href = mailtoUrl;
    setSent(true);
  };

  const handleCopy = async () => {
    try {
      const { subject: finalSubject, body } = buildEmailParts(user, category, subject, message);
      const fullText = `To: ${FEEDBACK_EMAIL}\nSubject: ${finalSubject}\n\n${body}`;
      await navigator.clipboard.writeText(fullText);
      setCopyStatus("copied");
    } catch (err) {
      console.error("Copy feedback failed:", err);
      setCopyStatus("error");
    } finally {
      setTimeout(() => setCopyStatus(null), 2000);
    }
  };

  return (
    <div className="flex-1 min-w-0 h-full overflow-y-auto bg-gray-950 text-white">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8 sm:py-12">

        <div className="flex items-start sm:items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-3 min-w-0">
            <Logo size={36} className="hidden sm:block" />
            <div className="min-w-0">
              <h1 className="text-2xl sm:text-3xl font-bold truncate">Feedback</h1>
              <p className="text-gray-500 text-sm mt-1">
                Report a bug, request a feature, or tell us what's on your mind.
              </p>
            </div>
          </div>

          {onClose && (
            <button
              onClick={onClose}
              className="text-sm bg-gray-900 hover:bg-gray-800 border border-gray-800 px-4 py-2 rounded-xl transition shrink-0"
            >
              Back
            </button>
          )}
        </div>

        <form onSubmit={handleSend} className="bg-gray-900/60 border border-gray-800 rounded-2xl p-5 sm:p-6 space-y-5">

          {/* CATEGORY */}
          <div>
            <label className="text-xs text-gray-500 uppercase tracking-wide mb-2 block">Category</label>
            <div className="flex flex-wrap gap-2">
              {CATEGORIES.map((c) => (
                <button
                  type="button"
                  key={c.id}
                  onClick={() => setCategory(c.id)}
                  className={`text-sm px-3.5 py-2 rounded-xl border transition ${
                    category === c.id
                      ? "bg-blue-600 border-blue-500 text-white"
                      : "bg-gray-800 border-gray-700 text-gray-300 hover:bg-gray-750"
                  }`}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </div>

          {/* SUBJECT */}
          <div>
            <label htmlFor="feedback-subject" className="text-xs text-gray-500 uppercase tracking-wide mb-2 block">
              Subject <span className="normal-case text-gray-600">(optional)</span>
            </label>
            <input
              id="feedback-subject"
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Short summary"
              className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-2.5 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-blue-500"
            />
          </div>

          {/* MESSAGE */}
          <div>
            <label htmlFor="feedback-message" className="text-xs text-gray-500 uppercase tracking-wide mb-2 block">
              Message
            </label>
            <textarea
              id="feedback-message"
              value={message}
              onChange={(e) => { setMessage(e.target.value); if (error) setError(null); }}
              rows={6}
              placeholder="What happened, or what would you like to see?"
              className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-blue-500 resize-none"
            />
            {error && <p className="text-xs text-red-400 mt-1.5">{error}</p>}
          </div>

          {/* DESTINATION */}
          <p className="text-xs text-gray-500">
            Sends to <span className="text-gray-300">{FEEDBACK_EMAIL}</span> via your email app.
          </p>

          {/* ACTIONS */}
          <div className="flex flex-col sm:flex-row gap-3 pt-1">
            <button
              type="submit"
              className="flex-1 bg-blue-600 hover:bg-blue-700 transition rounded-xl py-2.5 font-semibold text-sm"
            >
              Send Feedback
            </button>
            <button
              type="button"
              onClick={handleCopy}
              className="text-sm bg-gray-800 hover:bg-gray-700 border border-gray-700 px-4 py-2.5 rounded-xl transition"
            >
              {copyStatus === "copied" ? "Copied ✓" : copyStatus === "error" ? "Couldn't copy" : "Copy instead"}
            </button>
          </div>

          {sent && (
            <p className="text-xs text-emerald-400">
              Your email app should now be open with this feedback ready to send. If nothing
              opened, use "Copy instead" and paste it into an email to {FEEDBACK_EMAIL}.
            </p>
          )}

        </form>

      </div>
    </div>
  );
}
